import { Hourglass, PullRequest } from '@carbon/icons-react';
import { Box, Link, Stack, Typography, useTheme } from '@mui/material';

import formatDuration from '../../../utils/format-duration';
import useTimeLeft from '../../interview/hooks/use-time-left';
import { Assessment, GitHubProvisioning } from '../gihub-coding-page';
import { GithubCodingCard } from './github-coding-components';

const HIGLIGHT_MINUTES = 15;

const shouldShowRepoLink = (provisioning?: GitHubProvisioning | null) => {
  return !!(provisioning?.repoName && provisioning?.repoUrl);
};

const shouldShowTimer = (assessment: Assessment) => {
  return !!(assessment.startedAt && !assessment.completedAt);
};

export const Timer = ({ assessment }: { assessment: Assessment }) => {
  const theme = useTheme();
  const showTimer = shouldShowTimer(assessment);

  const startedAt = assessment.startedAt
    ? new Date(assessment.startedAt)
    : null;
  const duration = assessment.template.duration ?? null;
  const timeLeftSeconds = useTimeLeft({
    startedAt,
    duration,
  });
  const color =
    (timeLeftSeconds ?? 0) < HIGLIGHT_MINUTES * 60
      ? theme.palette.error.main
      : 'inherit';

  if (!showTimer || timeLeftSeconds === null) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box style={{ width: 32, display: 'flex', justifyContent: 'center' }}>
        <Hourglass size={20} />
      </Box>
      <Typography variant='body1'>
        Time left to submit:{' '}
        <strong style={{ color }}>{formatDuration(timeLeftSeconds)}</strong>
      </Typography>
    </Box>
  );
};

const RepoLink = ({
  provisioning,
}: {
  provisioning?: GitHubProvisioning | null;
}) => {
  if (!shouldShowRepoLink(provisioning)) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box style={{ width: 32, display: 'flex', justifyContent: 'center' }}>
        <PullRequest size={20} />
      </Box>
      <Typography variant='body1'>
        Repository:{' '}
        <Link
          href={provisioning?.repoUrl ?? ''}
          target='_blank'
          rel='noopener noreferrer'
        >
          {provisioning?.repoName}
        </Link>
      </Typography>
    </Box>
  );
};

export const GithubCodingQuickLink = ({
  assessment,
  provisioning,
}: {
  assessment: Assessment;
  provisioning?: GitHubProvisioning | null;
}) => {
  if (!shouldShowRepoLink(provisioning) && !shouldShowTimer(assessment)) {
    return null;
  }

  return (
    <GithubCodingCard>
      <Stack direction='column' spacing={2}>
        <RepoLink provisioning={provisioning} />
        <Timer assessment={assessment} />
      </Stack>
    </GithubCodingCard>
  );
};
